import * as XLSX from "xlsx";

/**
 * Reads a CSV / XLSX / XLS file and returns its header row plus raw row objects.
 * Dates are parsed into Date objects so Excel serial numbers survive the round trip.
 */
export function parseSpreadsheet(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the selected file"));
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: "array", cellDates: true });
        const sheetName = wb.SheetNames[0];
        if (!sheetName) return reject(new Error("The file has no sheets"));
        const ws = wb.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false, dateNF: "yyyy-mm-dd" });
        const matrix = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        const headers = (matrix[0] || []).map((h) => String(h).trim()).filter(Boolean);
        if (headers.length === 0) return reject(new Error("The first row must contain column headings"));
        resolve({ headers, rows, sheetName });
      } catch (err) {
        reject(new Error("Unsupported or corrupt file. Use a .csv, .xlsx or .xls file."));
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

const normalize = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Abbreviated headings used by the register tables (and therefore by CSV/Excel exports)
 * mapped onto the full field labels, so an exported file re-imports without manual mapping.
 */
const HEADER_ALIASES = {
  dept: "department",
  class: "classification",
  obsid: "observationid",
  recycle: "recycling",
  generatedt: "generatedtonnes",
  recycledt: "recycledtonnes",
  rigsite: "site",
};

/**
 * Best-effort match of file headings onto register fields, by label then by key.
 * Returns { [fieldKey]: headerName }, omitting fields with no confident match.
 */
export function autoMapColumns(headers, fields) {
  const byNormalized = new Map();
  headers.forEach((h) => {
    const n = normalize(h);
    if (!n) return;
    if (!byNormalized.has(n)) byNormalized.set(n, h);
    const alias = HEADER_ALIASES[n];
    if (alias && !byNormalized.has(alias)) byNormalized.set(alias, h);
  });

  const mapping = {};
  const claimed = new Set();

  // Pass 1 — exact matches on label or key win, and reserve their heading.
  const unmatched = [];
  fields.forEach((f) => {
    const hit = byNormalized.get(normalize(f.label)) || byNormalized.get(normalize(f.key));
    if (hit) {
      mapping[f.key] = hit;
      claimed.add(hit);
    } else {
      unmatched.push(f);
    }
  });

  // Pass 2 — partial matches, e.g. a "Due" heading for a "dueDate" field. Headings
  // already taken by an exact match are off limits so "Due Date" cannot steal "Date".
  unmatched.forEach((f) => {
    const targets = [normalize(f.key), normalize(f.label)].filter(Boolean);
    let best = null;
    headers.forEach((h) => {
      if (claimed.has(h)) return;
      const nh = normalize(h);
      if (nh.length <= 2) return;
      const hits = targets.some((t) => nh.includes(t) || t.includes(nh));
      if (hits && (!best || nh.length > normalize(best).length)) best = h;
    });
    if (best) {
      mapping[f.key] = best;
      claimed.add(best);
    }
  });

  return mapping;
}

function toDateString(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  const str = String(value ?? "").trim();
  if (!str) return "";
  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // Excel serial date
  if (/^\d+(\.\d+)?$/.test(str)) {
    const serial = Number(str);
    if (serial > 20000 && serial < 60000) {
      const parsed = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
      return toDateString(parsed);
    }
  }
  // dd/mm/yyyy or dd-mm-yyyy
  const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`;
  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) return toDateString(parsed);
  return str;
}

/**
 * Turns raw file rows into register records using the column mapping.
 * Blank rows are dropped; rows without an ID get a generated one.
 */
export function buildRecords(rawRows, fields, mapping, idPrefix) {
  const records = [];
  let skipped = 0;
  let generatedIds = 0;

  rawRows.forEach((raw, index) => {
    const record = {};
    let hasValue = false;

    fields.forEach((f) => {
      const header = mapping[f.key];
      let value = header ? raw[header] : undefined;
      if (value === undefined || value === null) value = "";
      if (f.type === "date") value = toDateString(value);
      else if (f.type === "number") {
        const str = String(value).trim();
        value = str === "" ? "" : (Number.isNaN(Number(str)) ? str : Number(str));
      } else value = typeof value === "string" ? value.trim() : value;

      record[f.key] = value;
      if (f.key !== "id" && value !== "" && value !== null) hasValue = true;
    });

    if (!hasValue) {
      skipped += 1;
      return;
    }

    if (!record.id) {
      record.id = `${idPrefix}-${Date.now().toString().slice(-6)}${String(index).padStart(2, "0")}`;
      generatedIds += 1;
    } else {
      record.id = String(record.id).trim();
    }

    records.push(record);
  });

  return { records, skipped, generatedIds };
}

/**
 * Merges imported records into the existing register according to the chosen mode.
 */
export function mergeRecords(existing, incoming, mode) {
  if (mode === "replace") {
    return { rows: incoming, added: incoming.length, updated: 0, skipped: 0 };
  }

  const byId = new Map(existing.map((r) => [String(r.id), r]));
  let added = 0;
  let updated = 0;
  let skipped = 0;
  const appended = [];

  incoming.forEach((rec) => {
    const key = String(rec.id);
    if (byId.has(key)) {
      if (mode === "merge") {
        byId.set(key, { ...byId.get(key), ...rec });
        updated += 1;
      } else {
        skipped += 1;
      }
      return;
    }
    byId.set(key, rec);
    appended.push(rec);
    added += 1;
  });

  const existingUpdated = existing.map((r) => byId.get(String(r.id)) || r);
  return { rows: [...appended, ...existingUpdated], added, updated, skipped };
}

/**
 * Downloads an empty spreadsheet with one column per field, so users know the expected headings.
 */
export function downloadTemplate(fields, filename, sheetName = "Template") {
  const headerRow = fields.reduce((acc, f) => ({ ...acc, [f.label]: "" }), {});
  const ws = XLSX.utils.json_to_sheet([headerRow]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, filename);
}
